import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCRMData } from '../context/CRMDataContext';
import { useTeam } from '../context/TeamContext';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import DealCard from '../components/pipeline/DealCard';
import DealDrawer from '../components/pipeline/DealDrawer';
import { formatCurrency } from '../utils/helpers';
import BaseModal from '../components/modals/BaseModal';
import CompanyForm from '../components/forms/CompanyForm';
import DealForm from '../components/forms/DealForm';
import ActivityForm from '../components/forms/ActivityForm';
import AICopilotWidget from '../components/common/AICopilotWidget';
import FilterSidebar from '../components/common/FilterSidebar';

const PIPELINE_STAGES_STORAGE_KEY = 'saleswork_pipeline_stages';
const PIPELINE_CARD_DISPLAY_MODE_KEY = 'saleswork_pipeline_card_display_mode';
const ACTIVE_PROJECT_TITLE_KEY = 'saleswork_active_project_title';
const NEW_DEAL_MODAL_OPEN_KEY = 'saleswork_new_deal_modal_open';
const isProduction = import.meta.env.PROD === true;

const getStoredStages = fallbackStages => {
  try {
    const stored = localStorage.getItem(PIPELINE_STAGES_STORAGE_KEY);
    if (!stored) return Array.isArray(fallbackStages) ? fallbackStages : [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('[PIPELINE STAGES LOAD]', error);
    return Array.isArray(fallbackStages) ? fallbackStages : [];
  }
};

const getStoredCardDisplayMode = () => {
  try {
    const stored = localStorage.getItem(PIPELINE_CARD_DISPLAY_MODE_KEY);
    return stored === 'contact' || stored === 'company' ? stored : 'company';
  } catch (error) {
    console.error('[PIPELINE CARD DISPLAY MODE LOAD]', error);
    return 'company';
  }
};

const getStoredNewDealModalOpen = () => {
  try {
    return sessionStorage.getItem(NEW_DEAL_MODAL_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
};

const persistNewDealModalOpen = () => {
  try {
    sessionStorage.setItem(NEW_DEAL_MODAL_OPEN_KEY, 'true');
  } catch {}
};

const clearNewDealModalState = () => {
  try {
    sessionStorage.removeItem(NEW_DEAL_MODAL_OPEN_KEY);
    sessionStorage.removeItem('saleswork_new_deal_draft');
  } catch {}
};

const persistStages = updatedStages => {
  try {
    localStorage.setItem(PIPELINE_STAGES_STORAGE_KEY, JSON.stringify(updatedStages));
    return { success: true, storage: 'localStorage', key: PIPELINE_STAGES_STORAGE_KEY };
  } catch (error) {
    console.error('[PIPELINE PERSIST RESULT]', error);
    return { success: false, error };
  }
};

const getStageId = stage => stage?.stage_id || stage?.id;
const getDealStageId = (deal, firstStageId = null) => (
  deal?.stage_id || deal?.stageId || deal?.pipelineStageId || firstStageId
);

const normalizeSupabaseStages = stages => (
  (Array.isArray(stages) ? stages : []).map((stage, index) => ({
    ...stage,
    stage_id: stage.id,
    order: stage.sort_order ?? index
  }))
);

const PipelineLeadModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen || typeof document === 'undefined') return null;

  const handleBackdropClick = event => {
    if (event.target === event.currentTarget) onClose?.('backdrop');
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={handleBackdropClick} aria-hidden="true" />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl sm:my-8 sm:align-middle w-full max-w-4xl relative z-10" onClick={event => event.stopPropagation()}>
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white">
            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">{title}</h3>
            <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => onClose?.('x')}>
              <span className="sr-only">Lukk</span>
              <SafeIcon icon="✕" className="h-6 w-6 text-lg" />
            </button>
          </div>
          <div className="px-6 py-4 bg-white">{children}</div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Pipeline = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project_id');
  const pipelineId = searchParams.get('pipeline_id');
  const { users } = useTeam();
  const {
    deals,
    activities,
    pipelineStages: contextPipelineStages,
    companies,
    contacts,
    clientPipelines,
    updateDeal
  } = useCRMData();

  const currentPipeline = Array.isArray(clientPipelines)
    ? clientPipelines.find(pipeline => (
      pipelineId && String(pipeline.id || '') === String(pipelineId)
    ))
    : null;
  const hasPipelineContext = Boolean(projectId || pipelineId);
  const pipelineContextMissing = Boolean(pipelineId && !currentPipeline);
  const pipelineOwnerCompanyId = currentPipeline?.owner_company_id || currentPipeline?.ownerCompanyId || null;

  const [stages, setStages] = useState(() => isProduction ? [] : getStoredStages(contextPipelineStages));
  const [cardDisplayMode, setCardDisplayMode] = useState(getStoredCardDisplayMode);
  const [projectTitle, setProjectTitle] = useState(() => {
    try {
      return localStorage.getItem(ACTIVE_PROJECT_TITLE_KEY) || 'Pipeline';
    } catch {
      return 'Pipeline';
    }
  });
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(getStoredNewDealModalOpen);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [targetStageId, setTargetStageId] = useState(null);
  const [isAddingStage, setIsAddingStage] = useState(false);
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('bg-blue-100 text-blue-900 border-blue-200');
  const [editingStage, setEditingStage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pipelineError, setPipelineError] = useState('');
  const [activeFilters, setActiveFilters] = useState({});

  useEffect(() => {
    if (getStoredNewDealModalOpen()) setIsDealModalOpen(true);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(PIPELINE_CARD_DISPLAY_MODE_KEY, cardDisplayMode);
    } catch (error) {
      console.error('[PIPELINE CARD DISPLAY MODE SAVE]', error);
    }
  }, [cardDisplayMode]);

  useEffect(() => {
    if (!isProduction) return undefined;
    let isMounted = true;

    const loadSupabaseStages = async () => {
      const { data, error } = await supabase
        .from('pipeline_stages_1710925200000')
        .select('id,name,sort_order,color')
        .order('sort_order', { ascending: true });

      if (!isMounted) return;
      if (error) {
        console.warn('[PIPELINE STAGES] Supabase stages kunne ikke lastes.', error.message);
        setStages([]);
        return;
      }
      setStages(normalizeSupabaseStages(data));
    };

    loadSupabaseStages();
    return () => {
      isMounted = false;
    };
  }, []);

  const availableFilters = [
    { key: 'title', label: 'Navn (Tittel)', type: 'text' },
    { key: 'owner', label: 'Eier (Ansvarlig)', type: 'select', options: users.map(u => ({ value: u.user_id, label: u.name })) },
    { key: 'company', label: 'Selskap', type: 'text' },
    { key: 'value', label: 'Verdi (Beløp)', type: 'number' },
    { key: 'status', label: 'Status', type: 'select', options: [{ value: 'Åpen', label: 'Åpen' }, { value: 'Vunnet', label: 'Vunnet' }, { value: 'Tapt', label: 'Tapt' }] },
    { key: 'probability', label: 'Sannsynlighet (%)', type: 'number' },
    { key: 'source', label: 'Kilde', type: 'text' },
    { key: 'created_at', label: 'Opprettet dato', type: 'date' },
    { key: 'expected_close_date', label: 'Forventet lukking', type: 'date' },
    { key: 'phone', label: 'Telefon', type: 'text' },
    { key: 'email', label: 'E-post', type: 'text' }
  ];

  const safeStages = Array.isArray(stages) ? stages : [];
  const safeDeals = Array.isArray(deals) ? deals : [];
  const visibleDeals = safeDeals.filter(deal => deal.archived !== true && deal.status !== 'Arkivert');
  const pipelineStages = [...safeStages];
  const firstStageId = getStageId(pipelineStages[0]) || null;

  const filteredDeals = useMemo(() => visibleDeals.filter(deal => {
    if (pipelineId && String(deal.pipeline_id || deal.pipelineId || '') !== String(pipelineId)) return false;

    for (const [key, filterValue] of Object.entries(activeFilters)) {
      if (!filterValue) continue;
      const dealValue = key === 'owner'
        ? deal.owner_user_id
        : key === 'company'
          ? companies.find(c => c.company_id === deal.company_id)?.name
          : deal[key];

      if (filterValue === '__EMPTY__' && dealValue) return false;
      if (filterValue === '__NOT_EMPTY__' && !dealValue) return false;
      if (typeof dealValue === 'string' && !dealValue.toLowerCase().includes(filterValue.toLowerCase())) return false;
      if (typeof dealValue === 'number' && dealValue < Number(filterValue)) return false;
    }

    return true;
  }), [visibleDeals, activeFilters, companies, pipelineId]);

  const getStageTotal = stageId => filteredDeals
    .filter(deal => getDealStageId(deal, firstStageId) === stageId && deal.status === 'Åpen')
    .reduce((sum, deal) => sum + Number(deal.value), 0);

  const addPipelineStage = (name, color) => {
    const trimmedName = name?.trim();
    if (!trimmedName) {
      setPipelineError('Fasenavn må fylles ut før fasen kan lagres.');
      return { success: false };
    }

    const stageId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString();
    const updatedStages = [...safeStages, {
      id: stageId,
      stage_id: stageId,
      name: trimmedName,
      color: color || 'bg-blue-100 text-blue-900 border-blue-200',
      order: safeStages.length,
      leads: [],
      createdAt: new Date().toISOString()
    }];

    setStages(updatedStages);
    return persistStages(updatedStages);
  };

  const handleDragOver = e => e.preventDefault();

  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData('dealId');
    if (!dealId || !stageId) return;

    const result = await updateDeal(dealId, {
      stage_id: stageId,
      stageId,
      updatedAt: new Date().toISOString()
    });

    if (result?.success === false) setPipelineError(result.error?.message || 'Kunne ikke flytte kortet.');
  };

  const handleDeleteDeal = async deal => {
    const dealId = deal?.deal_id || deal?.id;
    if (!dealId || !window.confirm('Vil du slette dette kortet fra pipeline?')) return;

    const result = await updateDeal(dealId, {
      archived: true,
      updatedAt: new Date().toISOString()
    });

    if (result?.success === false) {
      setPipelineError(result.error?.message || 'Kunne ikke slette kortet.');
      return;
    }

    if (selectedDeal?.deal_id === dealId || selectedDeal?.id === dealId) setSelectedDeal(null);
  };

  const handleArchiveCard = async deal => {
    const dealId = deal?.deal_id || deal?.id;
    if (!dealId) return;

    const result = await updateDeal(dealId, {
      status: 'Arkivert',
      updatedAt: new Date().toISOString()
    });

    if (result?.success === false) {
      setPipelineError(result.error || 'Kunne ikke oppdatere kortet.');
      return;
    }

    if (selectedDeal?.deal_id === dealId || selectedDeal?.id === dealId) setSelectedDeal(null);
  };

  const openNewDealModal = stageId => {
    setEditingDeal(null);
    setTargetStageId(stageId || null);
    persistNewDealModalOpen();
    setIsDealModalOpen(true);
  };

  const closeNewDealModal = () => {
    setIsDealModalOpen(false);
    clearNewDealModalState();
  };

  const closeDealModal = () => {
    if (!editingDeal) {
      closeNewDealModal();
      return;
    }
    setIsDealModalOpen(false);
  };

  const newDealContext = {
    stage_id: targetStageId || firstStageId,
    pipeline_id: pipelineId || null,
    pipelineId: pipelineId || null,
    pipelineName: currentPipeline?.name || null,
    client_company_id: pipelineOwnerCompanyId,
    clientCompanyId: pipelineOwnerCompanyId
  };

  const pipelinePageTitle = pipelineId && currentPipeline ? currentPipeline.name : projectTitle;
  const pipelineSubtitle = pipelineId && currentPipeline
    ? `Prosjektpipeline · ${filteredDeals.length} lead${filteredDeals.length === 1 ? '' : 's'}`
    : null;

  return (
    <div className="h-full flex flex-col relative bg-slate-50/50">
      <div className="px-6 py-3 flex-none space-y-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-black text-slate-900 flex items-center tracking-tight">
              {pipelinePageTitle}
              <span className="ml-4 text-sm font-medium bg-slate-100 px-3 py-1 rounded-full text-slate-500 border border-slate-200">
                {filteredDeals.length} leads
              </span>
            </h1>
            {pipelineSubtitle ? (
              <div className="mt-2 text-xs font-semibold text-slate-400">{pipelineSubtitle}</div>
            ) : hasPipelineContext && (
              <div className="mt-2 text-xs font-semibold text-slate-400">
                {pipelineContextMissing ? 'Pipeline-kontekst ikke funnet' : 'Pipeline for prosjekt'}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 mt-4 md:mt-0">
            <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              <span className="px-2 text-xs font-bold text-slate-500">Vis:</span>
              <button
                type="button"
                onClick={() => setCardDisplayMode('company')}
                aria-pressed={cardDisplayMode === 'company'}
                className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${cardDisplayMode === 'company' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                Firma
              </button>
              <button
                type="button"
                onClick={() => setCardDisplayMode('contact')}
                aria-pressed={cardDisplayMode === 'contact'}
                className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${cardDisplayMode === 'contact' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                Kontakt
              </button>
            </div>
            <button onClick={() => setIsFilterOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all border bg-white text-slate-600 border-slate-200 hover:border-slate-300">
              <SafeIcon icon="⚡" className="w-4 h-4" /> Filter
            </button>
            <button onClick={() => openNewDealModal()} className="flex items-center px-6 py-2.5 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-200 text-sm tracking-wide">
              <SafeIcon icon="➕" className="w-4 h-4 mr-2" /> Ny Lead
            </button>
          </div>
        </div>

        {pipelineId && currentPipeline && (
          <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs text-slate-500">
                Denne visningen viser kun leads som tilhører denne pipelinen.
              </p>
              <p className="mt-2 text-xs font-semibold text-slate-600">
                {filteredDeals.length} leads i denne visningen
              </p>
            </div>
            {projectId && (
              <button
                type="button"
                onClick={() => navigate(`/projects/${encodeURIComponent(projectId)}`)}
                className="shrink-0 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 transition-all hover:bg-slate-50"
              >
                Tilbake til prosjekt
              </button>
            )}
          </div>
        )}

        <AICopilotWidget
          deals={filteredDeals}
          activities={activities}
          pipelineId={pipelineId || null}
          isScopedPipeline={Boolean(pipelineId)}
          onOpenDeal={deal => setSelectedDeal(deal)}
        />
        {pipelineError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{pipelineError}</div>}
      </div>

      <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden h-[calc(100vh-180px)] min-h-[620px] pb-4 px-6">
        <div className="grid grid-flow-col auto-cols-[minmax(280px,1fr)] gap-6 items-stretch h-full min-h-0 min-w-max">
          {pipelineStages.sort((a, b) => a.order - b.order).map(stage => {
            const stageId = getStageId(stage);
            const stageDeals = filteredDeals.filter(deal => getDealStageId(deal, firstStageId) === stageId);
            return (
              <div key={stageId} className="w-[340px] h-full min-h-0 grid grid-rows-[auto_minmax(0,1fr)_auto]" onDragOver={handleDragOver} onDrop={e => handleDrop(e, stageId)}>
                <div className="flex justify-between items-center mb-2 px-3 py-2 rounded-xl border bg-slate-100 text-slate-700 border-slate-200 shadow-sm">
                  <h3 className="font-black text-sm uppercase tracking-widest truncate">{stage.name}</h3>
                  <button type="button" onClick={() => openNewDealModal(stageId)} className="flex items-center justify-center gap-1.5 h-9 min-h-[36px] px-3 rounded-lg border border-slate-900/15 bg-white hover:bg-slate-50 text-slate-900 font-black text-xs uppercase tracking-wider shadow-sm transition-colors">
                    <SafeIcon icon="➕" className="w-3.5 h-3.5" /> NY
                  </button>
                </div>
                <div className="min-h-0 max-h-[calc(100vh-300px)] overflow-y-auto space-y-2 custom-scrollbar pr-1 pb-3">
                  {stageDeals.map(deal => (
                    <DealCard
                      key={deal.deal_id}
                      deal={deal}
                      company={companies.find(c => c.company_id === deal.company_id)}
                      contact={contacts.find(c => c.contact_id === deal.contact_id)}
                      onEdit={setSelectedDeal}
                      onDelete={handleDeleteDeal}
                      onArchive={handleArchiveCard}
                      stageName={stage.name}
                      displayMode={cardDisplayMode}
                    />
                  ))}
                </div>
                <div className="shrink-0 border-t border-blue-100 pt-3 flex justify-center">
                  <div className="inline-flex items-center gap-1 rounded-lg border border-blue-100 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Verdi:</span>
                    <span className="text-xs font-black text-slate-900">{formatCurrency(getStageTotal(stageId), false)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <FilterSidebar isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} availableFilters={availableFilters} activeFilters={activeFilters} onFilterChange={setActiveFilters} />

      <DealDrawer
        isOpen={!!selectedDeal}
        deal={selectedDeal}
        company={companies.find(c => c.company_id === selectedDeal?.company_id)}
        contact={contacts.find(c => c.contact_id === selectedDeal?.contact_id)}
        pipelineStages={stages}
        isActivityModalOpen={isActivityModalOpen}
        onClose={() => setSelectedDeal(null)}
        onEdit={deal => {
          setSelectedDeal(null);
          setEditingDeal(deal);
          setIsDealModalOpen(true);
        }}
        onAddActivity={deal => {
          setEditingDeal(deal);
          setIsActivityModalOpen(true);
        }}
      />

      <PipelineLeadModal isOpen={isDealModalOpen} onClose={closeDealModal} title={editingDeal ? 'Rediger Lead' : 'Legg til ny Lead'}>
        <DealForm deal={editingDeal || newDealContext} onClose={editingDeal ? closeDealModal : closeNewDealModal} />
      </PipelineLeadModal>

      <BaseModal isOpen={isActivityModalOpen} onClose={() => setIsActivityModalOpen(false)} title="Ny Aktivitet">
        <ActivityForm onClose={() => setIsActivityModalOpen(false)} activity={{ deal_id: editingDeal?.deal_id, company_id: editingDeal?.company_id }} />
      </BaseModal>

      <BaseModal isOpen={isCompanyModalOpen} onClose={() => setIsCompanyModalOpen(false)} title="Rediger Kundeinfo">
        <CompanyForm company={editingCompany} onClose={() => setIsCompanyModalOpen(false)} />
      </BaseModal>
    </div>
  );
};

export default Pipeline;